import * as yup from 'yup';
import _get from 'lodash/get';
import _keys from 'lodash/keys';
import { COUPON_TYPE } from 'commons/models/coupon';
import {
	Row,
	RenderConfig,
	Column,
	TextInput,
	SelectInput,
	TextAreaInput,
	TimeIntervalInput,
	EMPTY_ARRAY,
	EMPTY_OBJECT,
	CASE,
	NumberInput,
	BOOLEAN_OPTIONS
} from 'commons-ui';

import { TYPE_OPTIONS, ALLOWED_PAYMENT_MODES_OPTIONS, ALLOWED_PLATFORMS_OPTIONS } from './constants';

export const userlistDependencyResolver = (props: any, dependencyValues: any) => {
	const isOpen = _get(dependencyValues, '0', false);
	const isEditable = props.isEditable[props.mode];
	const { disabled } = props;

	if (!isOpen && isEditable && !disabled) {
		return { disabled: true };
	}
	if (isOpen && isEditable === disabled) {
		return { disabled: !isEditable };
	}
	return null;
};

export const maxRedemptionPerPersonDependencyResolver = (props: any, dependencyValues: any) => {
	const allowUnlimitedRedemptionsPerPerson = _get(dependencyValues, '0', false);
	const isEditable = props.isEditable[props.mode];
	const { required, disabled } = props;

	if (allowUnlimitedRedemptionsPerPerson && isEditable && !disabled) {
		return { disabled: true };
	}

	if (allowUnlimitedRedemptionsPerPerson && required) {
		return { required: false };
	}

	if (!allowUnlimitedRedemptionsPerPerson && isEditable === disabled) {
		return { disabled: !isEditable };
	}

	if (!allowUnlimitedRedemptionsPerPerson && !required) {
		return { required: true };
	}

	return null;
};

export const maxRedemptionDependencyResolver = (props: any, dependencyValues: any) => {
	const allowUnlimitedRedemptions = _get(dependencyValues, '0', false);
	const isEditable = props.isEditable[props.mode];
	const { required, disabled } = props;

	if (allowUnlimitedRedemptions && isEditable && !disabled) {
		return { disabled: true };
	}

	if (allowUnlimitedRedemptions && required) {
		return { required: false };
	}

	if (!allowUnlimitedRedemptions && isEditable === disabled) {
		return { disabled: !isEditable };
	}

	if (!allowUnlimitedRedemptions && !required) {
		return { required: true };
	}

	return null;
};

export const segmentsDependencyResolver = (props: any, dependencyValues: any) => {
	const isCouponSegmented = _get(dependencyValues, '0', false);
	const isEditable = props.isEditable[props.mode];
	const { disabled } = props;

	if (!isCouponSegmented && isEditable && !disabled) {
		return { disabled: true };
	}

	if (isCouponSegmented && isEditable === disabled) {
		return { disabled: !isEditable };
	}

	return null;
};

export const validationSchemaCreate = yup.object().shape(
	{
		code: yup
			.string()
			.matches(/^[A-Z]/, 'Code should start with alphabet')
			.matches(/^([A-Z0-9])+$/, 'Code should only contain alphanumeric characters')
			.required('Code is a required field'),
		description: yup.string().required('Description is a required field'),
		validFrom: yup
			.number()
			.transform((value, originalValue) => {
				if (originalValue === '' || originalValue === null) {
					return undefined;
				}
				return originalValue;
			})
			.test({
				message: 'Valid from should be valid date',
				test: (value: any) => value !== '' || value !== null || !Number.isNaN(new Date(value).getTime())
			})
			.test({
				message: 'Valid from should be greater than current time',
				test: (value: any) => new Date(value) > new Date()
			})
			.required('Valid from is a required field'),
		validTill: yup
			.number()
			.transform((value, originalValue) => {
				if (originalValue === '' || originalValue === null) {
					return undefined;
				}
				return originalValue;
			})
			.test({
				message: 'Valid till should be valid date',
				test: (value: any) => value !== '' || value !== null || !Number.isNaN(new Date(value).getTime())
			})
			.test({
				message: 'Valid till should be greater than current time',
				test: (value: any) => new Date(value) > new Date()
			})
			.required('Valid till is a required field'),
		isOpen: yup.boolean().required('Only for specific users is a required field'),
		isFirstDeposit: yup.boolean().required(),
		allowUnlimitedRedemptions: yup.boolean().nullable(),
		maxRedemptions: yup.number().when('allowUnlimitedRedemptions', {
			is: true,
			then: yup.number().nullable(),
			otherwise: yup.number().min(1, 'Maximum Redemptions should be atleast 1').required()
		}),
		conversions: yup
			.array()
			.of(
				yup.object().shape({
					amount: yup.number().min(0, 'Should be a positive value').required('Amount is a required field'),
					unlockedCoins: yup.number().min(0, 'Should be a positive value').required('Unlocked Coins is a required field'),
					actualCoins: yup.number().min(0, 'Should be a positive value').required('Actual Coins is a required field'),
					addedCoins: yup.number().min(0, 'Should be a positive value').required('Added Coins is a required field'),
					cashbackCoins: yup.number().min(0, 'Should be a positive value').required('Cashback Coins is a required field')
				})
			)
			.test({
				message: 'Atleast one conversion should be present',
				test: (arr: any) => arr.length >= 1
			})
			.required(),
		maxConversionPerRedemption: yup
			.object()
			.shape({
				unlockedCoins: yup.number().min(0, 'Should be a positive value').required('Unlocked Coins is a required field'),
				actualCoins: yup.number().min(0, 'Should be a positive value').required('Actual Coins is a required field'),
				addedCoins: yup.number().min(0, 'Should be a positive value').required('Added Coins is a required field'),
				cashbackCoins: yup.number().min(0, 'Should be a positive value').required('Cashback Coins Coins is a required field')
			})
			.required(),
		allowUnlimitedRedemptionsPerPerson: yup
			.boolean()
			.when('isSecondDeposit', {
				is: true,
				then: yup.boolean().oneOf([false], 'Unlimited Redemptions per person can not be allowed if second deposit is true'),
				otherwise: yup.boolean().when('isFirstDeposit', {
					is: true,
					then: yup.boolean().oneOf([false], 'Unlimited Redemptions per person can not be allowed if first deposit is true')
				})
			})
			.nullable(),
		maxRedemptionsPerPerson: yup.number().when('isSecondDeposit', {
			is: true,
			then: yup.number().oneOf([1, 2], 'Maximum Redemptions Per Person should be 1 or 2 if second deposit is true').required(),
			otherwise: yup.number().when('isFirstDeposit', {
				is: true,
				then: yup.number().oneOf([1], 'Maximum Redemptions Per Person should be 1 if first deposit is true').required(),
				otherwise: yup.number().when('allowUnlimitedRedemptionsPerPerson', {
					is: true,
					then: yup.number().nullable(),
					otherwise: yup.number().min(1, 'Maximum Redemptions Per Person should be atleast 1').required()
				})
			})
		}),
		isHiddenFromApp: yup.boolean().required(),
		isSecondDeposit: yup.boolean().required(),
		redemptionInterval: yup.number().nullable(),
		type: yup
			.string()
			.nullable()
			.oneOf([..._keys(COUPON_TYPE), 'INSURANCE']),
		segmentsInclusive: yup.string().when(['isCouponSegmented', 'segmentsExclusive'], {
			is: (isCouponSegmented: boolean, segmentsExclusive?: string) =>
				isCouponSegmented && (!segmentsExclusive || segmentsExclusive.length === 0),
			then: yup.string().required('Atleast one inclusive or one exclusive segments is required for segmented coupons'),
			otherwise: yup.string().nullable()
		}),
		segmentsExclusive: yup.string().when(['isCouponSegmented', 'segmentsInclusive'], {
			is: (isCouponSegmented: boolean, segmentsInclusive?: string) =>
				isCouponSegmented && (!segmentsInclusive || segmentsInclusive.length === 0),
			then: yup.string().required('Atleast one inclusive or one exclusive segments is required for segmented coupons'),
			otherwise: yup.string().nullable()
		})
	},
	[['segmentsExclusive', 'segmentsInclusive']]
);

export const validationSchemaEdit = yup.object().shape(
	{
		description: yup.string().required('Description is a required field'),
		validTill: yup
			.number()
			.transform((value, originalValue) => {
				if (originalValue === '' || originalValue === null) {
					return undefined;
				}
				return originalValue;
			})
			.test({
				message: 'Valid till should be valid date',
				test: (value: any) => value !== '' || value !== null || !Number.isNaN(new Date(value).getTime())
			})
			.test({
				message: 'Valid till should be greater than current time',
				test: (value: any) => new Date(value) > new Date()
			})
			.required('Valid till is a required field'),
		isOpen: yup.boolean().required('Only for specific users is a required field'),
		isFirstDeposit: yup.boolean().required(),
		allowUnlimitedRedemptions: yup.boolean().nullable(),
		maxRedemptions: yup.number().when('allowUnlimitedRedemptions', {
			is: true,
			then: yup.number().nullable(),
			otherwise: yup
				.number()
				.min(1, 'Maximum Redemptions should be atleast 1')
				.required('Maximum Redemptions is required field')
		}),
		allowUnlimitedRedemptionsPerPerson: yup
			.boolean()
			.when('isSecondDeposit', {
				is: true,
				then: yup.boolean().oneOf([false], 'Unlimited Redemptions per person can not be allowed if second deposit is true'),
				otherwise: yup.boolean().when('isFirstDeposit', {
					is: true,
					then: yup.boolean().oneOf([false], 'Unlimited Redemptions per person can not be allowed if first deposit is true')
				})
			})
			.nullable(),
		maxRedemptionsPerPerson: yup.number().when('isSecondDeposit', {
			is: true,
			then: yup.number().oneOf([1, 2], 'Maximum Redemptions Per Person should be 1 or 2 if second deposit is true').required(),
			otherwise: yup.number().when('isFirstDeposit', {
				is: true,
				then: yup.number().oneOf([1], 'Maximum Redemptions Per Person should be 1 if first deposit is true').required(),
				otherwise: yup.number().when('allowUnlimitedRedemptionsPerPerson', {
					is: true,
					then: yup.number().nullable(),
					otherwise: yup.number().min(1, 'Maximum Redemptions Per Person should be atleast 1').required()
				})
			})
		}),
		isHiddenFromApp: yup.boolean().required(),
		isSecondDeposit: yup.boolean().required(),
		redemptionInterval: yup.number().nullable(),
		type: yup.string().nullable().oneOf(_keys(COUPON_TYPE)),
		segmentsInclusive: yup.string().when(['isCouponSegmented', 'segmentsExclusive'], {
			is: (isCouponSegmented: boolean, segmentsExclusive?: string) =>
				isCouponSegmented && (!segmentsExclusive || segmentsExclusive.length === 0),
			then: yup.string().required('Atleast one inclusive or one exclusive segments is required for segmented coupons'),
			otherwise: yup.string().nullable()
		}),
		segmentsExclusive: yup.string().when(['isCouponSegmented', 'segmentsInclusive'], {
			is: (isCouponSegmented: boolean, segmentsInclusive?: string) =>
				isCouponSegmented && (!segmentsInclusive || segmentsInclusive.length === 0),
			then: yup.string().required('Atleast one inclusive or one exclusive segments is required for segmented coupons'),
			otherwise: yup.string().nullable()
		})
	},
	[['segmentsExclusive', 'segmentsInclusive']]
);

export const FORM_FIELD_KEYS = {
	CODE: 'code',
	DESCRIPTION: 'description',
	TYPE: 'type',
	IS_OPEN: 'isOpen',
	IS_DISABLED: 'isDisabled',
	IS_HIDDEN_FROM_APP: 'isHiddenFromApp',
	IS_FIRST_DEPOSIT: 'isFirstDeposit',
	IS_SECOND_DEPOSIT: 'isSecondDeposit',
	BUCKET: 'bucket',
	USERLIST: 'userlist',
	ALLOW_UNLIMITED_REDEMPTIONS: 'allowUnlimitedRedemptions',
	MAX_REDEMPTIONS: 'maxRedemptions',
	REDEMPTION_INTERVAL: 'redemptionInterval',
	ALLOW_UNLIMITED_REDEMPTIONS_PER_PERSON: 'allowUnlimitedRedemptionsPerPerson',
	MAX_REDEMPTIONS_PER_PERSON: 'maxRedemptionsPerPerson',
	SORT_ORDER: 'sortOrder',
	VALID_FROM: 'validFrom',
	VALID_TILL: 'validTill',
	ALLOWED_PAYMENT_MODES: 'allowedPaymentModes',
	ALLOWED_PLATFORMS: 'allowedPlatforms',
	IS_COUPON_SEGMENTED: 'isCouponSegmented',
	SEGMENTS_INCLUSIVE: 'segmentsInclusive',
	SEGMENTS_EXCLUSIVE: 'segmentsExclusive',
	CONVERSIONS: 'conversions',
	AMOUNT: 'amount',
	TICKETS: 'tickets',
	UNLOCKED_COINS: 'unlockedCoins',
	BONUS_COINS: 'bonusCoins',
	ACTUAL_COINS: 'actualCoins',
	ADDED_COINS: 'addedCoins',
	CASHBACK_COINS: 'cashbackCoins',
	MAX_CONVERSION_PER_REDEMPTION: 'maxConversionPerRedemption',
	CASHBACK_REWARD_EXPIRY_IN_HOURS: 'cashbackExpiryInHours'
};

export const FORM_LAYOUT_CONFIG = [
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.CODE)]), new Column([new Row(FORM_FIELD_KEYS.TYPE)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.DESCRIPTION)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.IS_FIRST_DEPOSIT)]),
		new Column([new Row(FORM_FIELD_KEYS.IS_SECOND_DEPOSIT)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.IS_OPEN)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.USERLIST)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.IS_COUPON_SEGMENTED)]),
		new Column([new Row(FORM_FIELD_KEYS.SEGMENTS_INCLUSIVE)]),
		new Column([new Row(FORM_FIELD_KEYS.SEGMENTS_EXCLUSIVE)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.ALLOW_UNLIMITED_REDEMPTIONS)]),
		new Column([new Row(FORM_FIELD_KEYS.MAX_REDEMPTIONS)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.ALLOW_UNLIMITED_REDEMPTIONS_PER_PERSON)]),
		new Column([new Row(FORM_FIELD_KEYS.MAX_REDEMPTIONS_PER_PERSON)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.REDEMPTION_INTERVAL)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.SORT_ORDER)]),
		new Column([new Row(FORM_FIELD_KEYS.IS_HIDDEN_FROM_APP)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.VALID_FROM)]),
		new Column([new Row(FORM_FIELD_KEYS.VALID_TILL)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.ALLOWED_PAYMENT_MODES)])]),
	// new Row(FORM_FIELD_KEYS.ALLOWED_PLATFORMS),
	new Row(FORM_FIELD_KEYS.CONVERSIONS, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.AMOUNT)]),
		new Column([new Row(FORM_FIELD_KEYS.UNLOCKED_COINS)]),
		new Column([new Row(FORM_FIELD_KEYS.ACTUAL_COINS)]),
		new Column([new Row(FORM_FIELD_KEYS.ADDED_COINS)]),
		new Column([new Row(FORM_FIELD_KEYS.CASHBACK_COINS)])
	]),
	new Row(FORM_FIELD_KEYS.MAX_CONVERSION_PER_REDEMPTION, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.UNLOCKED_COINS)]),
		new Column([new Row(FORM_FIELD_KEYS.ACTUAL_COINS)]),
		new Column([new Row(FORM_FIELD_KEYS.ADDED_COINS)]),
		new Column([new Row(FORM_FIELD_KEYS.CASHBACK_COINS)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.CASHBACK_REWARD_EXPIRY_IN_HOURS)])])
];

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.CODE]: new RenderConfig()
		.setName('code')
		.setLabel('Code')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, letterCase: CASE.UPPERCASE, required: true })
		.setToolTip('Coupon Code'),
	[FORM_FIELD_KEYS.DESCRIPTION]: new RenderConfig()
		.setName('description')
		.setLabel('Description')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.CASHBACK_REWARD_EXPIRY_IN_HOURS]: new RenderConfig()
		.setName('cashbackExpiryInHours')
		.setLabel('Cashback Expiry (In Hours)')
		.setRenderer(NumberInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: false })
		.setToolTip('Cashback Expiry In Hours'),
	[FORM_FIELD_KEYS.TYPE]: new RenderConfig()
		.setName('type')
		.setLabel('Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			options: TYPE_OPTIONS
		}).setToolTip(`Regular: coupon is highlighted in blue on app (default coupons);
		Exclusive: special coupons highlighted in violet+blue (e.g reactivation coupons)`),
	[FORM_FIELD_KEYS.IS_OPEN]: new RenderConfig()
		.setName('isOpen')
		.setLabel('Only for specific userlist')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		})
		.setToolTip('Coupon will be available for given userlist'),
	[FORM_FIELD_KEYS.IS_HIDDEN_FROM_APP]: new RenderConfig()
		.setName('isHiddenFromApp')
		.setLabel('Is Hidden From App')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		}),
	[FORM_FIELD_KEYS.IS_FIRST_DEPOSIT]: new RenderConfig()
		.setName('isFirstDeposit')
		.setLabel('Is First Deposit')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		})
		.setToolTip('Is valid only for first deposit'),
	[FORM_FIELD_KEYS.IS_SECOND_DEPOSIT]: new RenderConfig()
		.setName('isSecondDeposit')
		.setLabel('Is Second Deposit')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		})
		.setToolTip('Is valid for first and second deposit'),
	[FORM_FIELD_KEYS.BUCKET]: new RenderConfig()
		.setName('bucket')
		.setLabel('Bucket')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true } })
		.setToolTip('User Segment name'),
	[FORM_FIELD_KEYS.USERLIST]: new RenderConfig()
		.setName('userlist')
		.setLabel('Userlist')
		.setRenderer(TextAreaInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['isOpen'],
			dependencyResolver: userlistDependencyResolver
		})
		.setToolTip('Comma Separated List of users. Existing users list will not be visible here'),
	[FORM_FIELD_KEYS.ALLOW_UNLIMITED_REDEMPTIONS]: new RenderConfig()
		.setName('allowUnlimitedRedemptions')
		.setLabel('Allow Unlimited Redemptions')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS
		})
		.setToolTip('Coupon can be used any number of times'),
	[FORM_FIELD_KEYS.MAX_REDEMPTIONS]: new RenderConfig()
		.setName('maxRedemptions')
		.setLabel('Maximum Redemptions')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['allowUnlimitedRedemptions'],
			dependencyResolver: maxRedemptionDependencyResolver
		})
		.setToolTip('Maximum number of total redemptions allowed'),
	[FORM_FIELD_KEYS.REDEMPTION_INTERVAL]: new RenderConfig()
		.setName('redemptionInterval')
		.setLabel('Redemption Interval')
		.setRenderer(TimeIntervalInput)
		.setRenderProps({
			isEditable: { create: true, edit: true }
		})
		.setToolTip('Applicable once in this time interval'),
	[FORM_FIELD_KEYS.ALLOW_UNLIMITED_REDEMPTIONS_PER_PERSON]: new RenderConfig()
		.setName('allowUnlimitedRedemptionsPerPerson')
		.setLabel('Allow Unlimited Redemptions Per Person')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS
		})
		.setToolTip('An user can apply coupon any number of times. Maximum Redemptions limit will be applied'),
	[FORM_FIELD_KEYS.MAX_REDEMPTIONS_PER_PERSON]: new RenderConfig()
		.setName('maxRedemptionsPerPerson')
		.setLabel('Maximum Redemptions Per Person')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['allowUnlimitedRedemptionsPerPerson'],
			dependencyResolver: maxRedemptionPerPersonDependencyResolver
		})
		.setToolTip('Maximum usage per person'),
	[FORM_FIELD_KEYS.SORT_ORDER]: new RenderConfig()
		.setName('sortOrder')
		.setLabel('Sort Order')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true }
		})
		.setToolTip('position (sort order) of coupon in app'),
	[FORM_FIELD_KEYS.VALID_FROM]: new RenderConfig()
		.setName('validFrom')
		.setLabel('Valid From')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			inputType: 'datetime-local',
			required: true
		}),
	[FORM_FIELD_KEYS.VALID_TILL]: new RenderConfig()
		.setName('validTill')
		.setLabel('Valid Till')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'datetime-local',
			required: true
		}),
	[FORM_FIELD_KEYS.ALLOWED_PAYMENT_MODES]: new RenderConfig()
		.setName('allowedPaymentModes')
		.setLabel('Allowed Payment Modes')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: ALLOWED_PAYMENT_MODES_OPTIONS,
			multi: true
		})
		.setToolTip('Applicable on which payment modes'),
	[FORM_FIELD_KEYS.ALLOWED_PLATFORMS]: new RenderConfig()
		.setName('allowedPlatforms')
		.setLabel('Allowed Platforms')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: ALLOWED_PLATFORMS_OPTIONS,
			multi: true
		})
		.setToolTip('Applicable on which platforms'),
	[FORM_FIELD_KEYS.IS_COUPON_SEGMENTED]: new RenderConfig()
		.setName('isCouponSegmented')
		.setLabel('Is Coupon Segmented')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS
		})
		.setToolTip('Is coupon segmented'),
	[FORM_FIELD_KEYS.SEGMENTS_INCLUSIVE]: new RenderConfig()
		.setName('segmentsInclusive')
		.setLabel('Segments Inclusive')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['isCouponSegmented'],
			dependencyResolver: segmentsDependencyResolver
		})
		.setToolTip('Applicable only for given segments'),
	[FORM_FIELD_KEYS.SEGMENTS_EXCLUSIVE]: new RenderConfig()
		.setName('segmentsExclusive')
		.setLabel('Segments Exclusive')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['isCouponSegmented'],
			dependencyResolver: segmentsDependencyResolver
		})
		.setToolTip('Applicable for all segments other than given segments'),
	[FORM_FIELD_KEYS.CONVERSIONS]: new RenderConfig()
		.setName('conversions')
		.setLabel('Payout Details')
		.setRenderProps({
			isArrayField: true,
			isEditable: { create: true, edit: false },
			required: true
		}),
	[FORM_FIELD_KEYS.MAX_CONVERSION_PER_REDEMPTION]: new RenderConfig()
		.setName('maxConversionPerRedemption')
		.setLabel('Maximum Payout Per Redemption')
		.setRenderProps({ required: true })
		.setToolTip('Maximum payout per redemption'),
	[FORM_FIELD_KEYS.AMOUNT]: new RenderConfig()
		.setName('amount')
		.setLabel('Amount (in Paisa)')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true
		}),
	[FORM_FIELD_KEYS.TICKETS]: new RenderConfig()
		.setName('tickets')
		.setLabel('Tickets')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true
		}),
	[FORM_FIELD_KEYS.UNLOCKED_COINS]: new RenderConfig()
		.setName('unlockedCoins')
		.setLabel('Bonus Money (in Paisa)')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true
		}),
	[FORM_FIELD_KEYS.BONUS_COINS]: new RenderConfig()
		.setName('bonusCoins')
		.setLabel('Bonus Coins')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true
		}),
	[FORM_FIELD_KEYS.ACTUAL_COINS]: new RenderConfig()
		.setName('actualCoins')
		.setLabel('Winnings (in Paisa)')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true
		}),
	[FORM_FIELD_KEYS.ADDED_COINS]: new RenderConfig()
		.setName('addedCoins')
		.setLabel('Play Money (in Paisa)')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true
		}),
	[FORM_FIELD_KEYS.CASHBACK_COINS]: new RenderConfig()
		.setName('cashbackCoins')
		.setLabel('Cashback Money (in Paisa)')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true
		})
};

export const getFormDefaults = () => {
	return {
		isOpen: false,
		isDisabled: true,
		isHiddenFromApp: false,
		isFirstDeposit: false,
		isSecondDeposit: false,
		conversions: [{}],
		maxConversionPerRedemption: EMPTY_OBJECT,
		cashbackExpiryInHours: null,
		allowedPaymentModes: ['UPI'],
		// allowedPlatforms: EMPTY_ARRAY,
		validFrom: new Date().getTime(),
		validTill: new Date().getTime()
	};
};
